import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Product {
  id: string
  name: string
  category: string
  sku: string
  revenue: number
  units_sold: number
  margin_percent: number
  performance_score: string
}

interface Insight {
  id: string
  product_id: string
  insight_type: string
  description: string
  impact: string
  recommendation: string
}

interface Statistics {
  total_products: number
  active_products: number
  top_performers: number
  underperformers: number
  avg_margin: number
  total_revenue: number
  total_units: number
  categories: number
}

export default function ProductIntelligencePage() {
  const [products, setProducts] = useState<Product[]>([])
  const [insights, setInsights] = useState<Insight[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'products' | 'insights'>('products')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [performanceRes, insightsRes, statsRes] = await Promise.all([
        fetch('/api/productintelligence/performance'),
        fetch('/api/productintelligence/insights'),
        fetch('/api/productintelligence/statistics')
      ])

      if (performanceRes.ok) setProducts(await performanceRes.json())
      if (insightsRes.ok) setInsights(await insightsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching product intelligence data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPerformanceBadgeClass = (score: string) => {
    const classes: Record<string, string> = {
      excellent: 'bg-green-100 text-green-800',
      good: 'bg-blue-100 text-blue-800',
      average: 'bg-yellow-100 text-yellow-800',
      poor: 'bg-red-100 text-red-800'
    }
    return classes[score.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const getImpactBadgeClass = (impact: string) => {
    const classes: Record<string, string> = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    }
    return classes[impact.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Product Intelligence</h1>
          <p className="mt-2 text-gray-600">AI-driven product performance analytics and optimization</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_products.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-blue-600">${(statistics.total_revenue / 1000000).toFixed(1)}M</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Margin</p>
              <p className="text-2xl font-bold text-green-600">{statistics.avg_margin.toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top Performers</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.top_performers}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('products')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'products'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Products ({products.length})
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'insights'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              AI Insights ({insights.length})
            </button>
          </nav>
        </div>

        {/* Products Table */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Units Sold</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Margin</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.slice(0, 20).map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{product.name}</p>
                      <p className="text-sm text-gray-500">{product.sku}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${product.revenue.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.units_sold.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${product.margin_percent >= 30 ? 'text-green-600' : product.margin_percent >= 20 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {product.margin_percent.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPerformanceBadgeClass(product.performance_score)}`}>
                        {product.performance_score}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Insights Cards */}
        {activeTab === 'insights' && (
          <div className="space-y-4">
            {insights.map((insight) => (
              <div key={insight.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-sm font-medium text-blue-600">{insight.insight_type}</span>
                    <h3 className="text-lg font-semibold text-gray-900 mt-1">{insight.description}</h3>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getImpactBadgeClass(insight.impact)}`}>
                    {insight.impact} impact
                  </span>
                </div>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800">Recommendation:</p>
                  <p className="text-sm text-blue-700 mt-1">{insight.recommendation}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
